import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, RefreshCw, Globe } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface FXRate {
  date: string;
  base_currency: string;
  rates: any; // JSON from database
}

interface FXSettingsProps {
  baseCurrency?: string;
}

export function FXSettings({ baseCurrency = 'USD' }: FXSettingsProps) {
  const [rates, setRates] = useState<FXRate | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [targetCurrency, setTargetCurrency] = useState(baseCurrency);
  const { toast } = useToast();

  async function loadRates() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('fx_rates')
        .select('date, base_currency, rates')
        .order('date', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      setRates(data);
    } catch (error: any) {
      toast({
        title: 'Error loading FX rates',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }

  async function refreshRates() {
    setUpdating(true);
    try {
      const { error } = await supabase.functions.invoke('fetch-fx-rates', {
        body: { base: targetCurrency },
      });

      if (error) throw error;

      toast({
        title: 'FX rates updated',
        description: `Successfully fetched latest rates for ${targetCurrency}`,
      });

      await loadRates();
    } catch (error: any) {
      toast({
        title: 'Error updating FX rates',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setUpdating(false);
    }
  }

  useEffect(() => {
    loadRates();
  }, []);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              FX Normalization Settings
            </CardTitle>
            <CardDescription>
              Manage currency exchange rates for multi-currency financials
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Select value={targetCurrency} onValueChange={setTargetCurrency}>
              <SelectTrigger className="w-[100px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="ZAR">ZAR</SelectItem>
                <SelectItem value="GBP">GBP</SelectItem>
                <SelectItem value="EUR">EUR</SelectItem>
                <SelectItem value="AED">AED</SelectItem>
                <SelectItem value="KES">KES</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={refreshRates} disabled={updating}>
              {updating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </>
              )}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm text-muted-foreground space-y-1">
          <p>
            <strong>Last updated:</strong>{' '}
            {rates?.date ? new Date(rates.date).toLocaleDateString() : 'No data available'}
          </p>
          <p>
            <strong>Base currency:</strong> {rates?.base_currency || '—'}
          </p>
        </div>

        {rates?.rates && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {Object.entries(rates.rates)
              .filter(([currency]) => ['USD', 'ZAR', 'GBP', 'EUR', 'AED', 'KES'].includes(currency))
              .map(([currency, rate]) => (
                <div
                  key={currency}
                  className="flex justify-between items-center border rounded-md p-3 bg-card"
                >
                  <span className="font-medium">{currency}</span>
                  <span className="text-muted-foreground">{Number(rate).toFixed(3)}</span>
                </div>
              ))}
          </div>
        )}

        {!rates && (
          <div className="text-center py-6 text-muted-foreground">
            <p>No FX rates available.</p>
            <p className="text-sm mt-1">Click "Refresh" to fetch the latest rates.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
